import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useContext,
} from 'react';
import { createPortal, unstable_batchedUpdates } from 'react-dom';
import WordArray from 'crypto-js/lib-typedarrays';
import Hex from 'crypto-js/enc-hex';
import sha1 from 'crypto-js/sha1';
import useWizardStore from '@zs/useWizardStore';
import { MainContext } from '@/rewrite/contexts/MainContext';
import * as common from '@/common';
import * as elements from '@svg/elements';
import {
  getDataTransferFiles,
} from '@/imageFiles';
import { Icon } from '@iconify/react';
import { HttpError } from '@/WebTools';
import { E2EManifest, ImageSyncService } from '../contexts/ImageSyncService';
import {
  GetParticipantStudyEyeResponse,
  RecordStudyEyeUploadInput,
  StudyEyeUploadSelection,
  StudyEyeValue,
} from '../types/studyEvents';
import { versionLabel } from '../version';


// =============================================================================
// Types
// =============================================================================

interface E2ELocal {
  file: File;
  sha1: string;
  dest?: string;
  processed?: boolean;
}

interface UploadProgress {
  filename: string;
  status: 'hashing' | 'uploading' | 'uploaded' | 'error';
  progress: number;
  error?: string;
}

interface ProcessingFile {
  filename: string;
  displayName: string;
  status: 'pending' | 'processing' | 'complete' | 'error';
  seriesCount?: number;
  error?: string;
  startedAt?: number;
}

interface UploadE2EProps {
  onComplete?: () => void
}

interface SubmissionIdentity {
  fingerprint: string;
  uuid: string;
}

const STUDY_EYE_OPTIONS: { value: StudyEyeValue; label: string }[] = [
  { value: 'OD', label: 'OD (Right)' },
  { value: 'OS', label: 'OS (Left)' },
  { value: 'NA', label: 'Not Available' },
];

async function recordStudyEyeSelection(
  imageSyncService: ImageSyncService,
  request: RecordStudyEyeUploadInput
): Promise<void> {
  try {
    await imageSyncService.recordStudyEyeUpload(request);
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === 409) {
      // Reusing the same submission UUID means the Study Eye event was already
      // recorded. Imaging can proceed without creating another event.
      return;
    }
    throw error;
  }
}

function isTechnicalStudyEyeError(error: unknown): boolean {
  return !(error instanceof HttpError) || error.statusCode >= 500;
}

function getStudyEyeRequestErrorMessage(error: unknown): string {
  if (!(error instanceof HttpError)) {
    return 'Study Eye could not be recorded for this Participant. Please try again.';
  }
  if (error.statusCode === 400) {
    return 'The Study Eye response was not accepted. Please review it and try again.';
  }
  if (error.statusCode === 403) {
    return 'You are not authorized to record the Study Eye for this Participant.';
  }
  return `Study Eye could not be recorded for this Participant (error ${error.statusCode}).`;
}

// =============================================================================
// Main Component
// =============================================================================

export const UploadE2E = ({
  onComplete,
}: UploadE2EProps): JSX.Element => {
  const {
    imageSyncService,
    crc_name: claimedOperatorName,
    session_uuid: sessionUuid,
  } = useContext(MainContext)!;
  const abortCon = useRef(new AbortController());
  const studyEyeTouchedRef = useRef(false);
  const submissionIdentityRef = useRef<SubmissionIdentity | null>(null);
  const sourceSha1ByRef = useRef<Map<string, string>>(new Map());
  const manifestVersionBySourceRef = useRef<Map<string, string>>(new Map());

  const selectionOD = useE2ESelector('OD');
  const selectionOS = useE2ESelector('OS');

  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState<{ done: number; total: number; phase: string } | null>(null);
  const [hasSubmittedImages, setHasSubmittedImages] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reconciliationWarning, setReconciliationWarning] = useState<string | null>(null);
  const [openSeries, setOpenSeries] = useState<any[]>([]);
  // Fail-safe default: stay disabled until the backend confirms this trial has
  // Study Eye capture enabled, so a failed or absent read never blocks imaging.
  const [studyEyeEnabled, setStudyEyeEnabled] = useState(false);
  const [studyEyeResponse, setStudyEyeResponse] = useState<GetParticipantStudyEyeResponse | null>(null);
  const [studyEye, setStudyEye] = useState<StudyEyeValue | null>(null);
  const [studyEyeReason, setStudyEyeReason] = useState('');

  // Progress tracking
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [processingFiles, setProcessingFiles] = useState<ProcessingFile[]>([]);

  // Check if all 6 selections are made
  const isSelectionComplete = useMemo(() => {
    return (
      selectionOD.octVolume[0] !== null &&
      selectionOD.fafField2.value !== null &&
      selectionOD.fafField1M.value !== null &&
      selectionOS.octVolume[0] !== null &&
      selectionOS.fafField2.value !== null &&
      selectionOS.fafField1M.value !== null
    );
  }, [
    selectionOD.octVolume[0],
    selectionOD.fafField2.value,
    selectionOD.fafField1M.value,
    selectionOS.octVolume[0],
    selectionOS.fafField2.value,
    selectionOS.fafField1M.value,
  ]);

  // Count how many selections are made
  const selectionCount = useMemo(() => {
    let count = 0;
    if (selectionOD.octVolume[0]) count++;
    if (selectionOD.fafField2.value) count++;
    if (selectionOD.fafField1M.value) count++;
    if (selectionOS.octVolume[0]) count++;
    if (selectionOS.fafField2.value) count++;
    if (selectionOS.fafField1M.value) count++;
    return count;
  }, [
    selectionOD.octVolume[0],
    selectionOD.fafField2.value,
    selectionOD.fafField1M.value,
    selectionOS.octVolume[0],
    selectionOS.fafField2.value,
    selectionOS.fafField1M.value,
  ]);

  const isStudyEyeValid = !studyEyeEnabled || (
    studyEye !== null &&
    (studyEye !== 'NA' || (
      studyEyeReason.trim().length >= 1 &&
      studyEyeReason.length <= 1000
    ))
  );

  const studyEyeReference = studyEyeResponse?.current
    ? {
      label: 'Current designation',
      value: studyEyeResponse.current.value,
    }
    : studyEyeResponse?.layout_baseline?.internally_consistent && studyEyeResponse.layout_baseline.value
    ? {
      label: 'Existing layout',
      value: studyEyeResponse.layout_baseline.value,
    }
    : null;

  useEffect(() => {
    let isActive = true;

    imageSyncService.getParticipantStudyEye()
      .then(response => {
        if (!isActive) return;

        setStudyEyeResponse(response);
        setStudyEyeEnabled(response.enabled);
        if (!response.enabled || studyEyeTouchedRef.current) return;

        const prefill = response.current?.value
          ?? (response.layout_baseline?.internally_consistent
            ? response.layout_baseline.value
            : null);
        if (!prefill) return;

        setStudyEye(prefill);
        if (prefill === 'NA' && response.current?.value === 'NA') {
          setStudyEyeReason(response.current.reason ?? '');
        }
      })
      .catch(() => {
        // Fail safe: if we cannot confirm the trial has Study Eye capture
        // enabled, keep the section disabled so a backend hiccup never blocks
        // image submission. Image loading is independent of this optional read.
        setStudyEyeEnabled(false);
        console.warn('The existing Study Eye designation could not be loaded.');
      });

    return () => {
      isActive = false;
    };
  }, [imageSyncService]);

  const handleStudyEyeChange = useCallback((value: StudyEyeValue) => {
    studyEyeTouchedRef.current = true;
    setStudyEye(value);
    setSubmitError(null);
  }, []);

  const handleStudyEyeReasonChange = useCallback((value: string) => {
    studyEyeTouchedRef.current = true;
    setStudyEyeReason(value);
    setSubmitError(null);
  }, []);

  const currentStudyEye = studyEyeResponse?.current;
  const handleSubmit = useCallback(async () => {
    if (
      isSubmitting ||
      hasSubmittedImages ||
      !isSelectionComplete ||
      !isStudyEyeValid ||
      (studyEyeEnabled && studyEye === null)
    ) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setReconciliationWarning(null);

    try {
      const findSeries = (id: string) => openSeries.find(s => s.series_id === id || s.previewPath === id);
      
      const odOct = findSeries(selectionOD.octVolume[0]!);
      const odFaf = findSeries(selectionOD.fafField2.value!);
      const odFafonh = findSeries(selectionOD.fafField1M.value!);
      const osOct = findSeries(selectionOS.octVolume[0]!);
      const osFaf = findSeries(selectionOS.fafField2.value!);
      const osFafonh = findSeries(selectionOS.fafField1M.value!);

      const selectedItems: {
        eye: 'OD' | 'OS';
        image_type: StudyEyeUploadSelection['image_type'];
        item: any;
      }[] = [
        { eye: 'OD', image_type: 'OCT', item: odOct },
        { eye: 'OD', image_type: 'FAF', item: odFaf },
        { eye: 'OD', image_type: 'FAFONH', item: odFafonh },
        { eye: 'OS', image_type: 'OCT', item: osOct },
        { eye: 'OS', image_type: 'FAF', item: osFaf },
        { eye: 'OS', image_type: 'FAFONH', item: osFafonh },
      ];
      if (selectedItems.some(({ item }) => !item?.source || !item?.series_id)) {
        setSubmitError('One or more selected images are no longer available. Please select the images again.');
        return;
      }

      const buildFAFSelection = (item: any) => {
        const seriesId = item.original_series_id || item.series_id;
        if (item.total_images > 1 && item.fundus_paths?.[0]) {
          return `${item.source}/${seriesId}:${item.fundus_paths[0]}`;
        }
        return `${item.source}/${seriesId}`;
      };

      const selections = {
        OD: {
          OCT: `${odOct.source}/${odOct.series_id}`,
          FAF: buildFAFSelection(odFaf),
          FAFONH: buildFAFSelection(odFafonh),
        },
        OS: {
          OCT: `${osOct.source}/${osOct.series_id}`,
          FAF: buildFAFSelection(osFaf),
          FAFONH: buildFAFSelection(osFafonh),
        },
      };

      const needsInterpolation = [odOct, osOct].some(oct => oct?.needs_interpolation === true);

      let studyEyeRequest: RecordStudyEyeUploadInput | null = null;
      let submissionUuid: string | null = null;
      if (studyEyeEnabled && studyEye) {
        const contextSelections: StudyEyeUploadSelection[] = selectedItems.map(({
          eye,
          image_type,
          item,
        }) => ({
          eye,
          image_type,
          source_ref: item.source,
          series_id: item.original_series_id || item.series_id,
        }));
        const selectedSourceRefs = Array.from(new Set(
          contextSelections.map(selection => selection.source_ref)
        ));
        const sources = selectedSourceRefs.flatMap(sourceRef => {
          const sourceSha1 = sourceSha1ByRef.current.get(sourceRef);
          return sourceSha1
            ? [{ source_ref: sourceRef, sha1: sourceSha1 }]
            : [];
        });
        const manifestVersions = Array.from(new Set(
          selectedSourceRefs.flatMap(sourceRef => {
            const manifestVersion = manifestVersionBySourceRef.current.get(sourceRef);
            return manifestVersion ? [manifestVersion] : [];
          })
        ));
        const captureDates = selectedItems
          .map(({ item }) => item.capture_date)
          .filter((captureDate): captureDate is string => (
            typeof captureDate === 'string' && captureDate.length > 0
          ));
        const uniqueCaptureDates = Array.from(new Set(captureDates)).sort();
        const requestWithoutUuid: Omit<RecordStudyEyeUploadInput, 'submission_uuid'> = {
          ...(currentStudyEye
            ? { expected_current_event_id: currentStudyEye.event_id }
            : {}),
          study_eye: {
            code: studyEye,
            reason: studyEye === 'NA' ? studyEyeReason.trim() : null,
          },
          ...(claimedOperatorName.trim()
            ? { claimed_operator_name: claimedOperatorName.trim() }
            : {}),
          context: {
            import_mode: 'e2e_selection',
            ...(sessionUuid ? { localizer_session_id: sessionUuid } : {}),
            client_build: versionLabel.replace(/[^\x20-\x7E]/g, '-'), // ASCII only: backend rejects the version label's non-ASCII middot separators
            manifest_versions: manifestVersions,
            capture_date_status: uniqueCaptureDates.length === 0
              ? 'unavailable'
              : uniqueCaptureDates.length === 1
              ? 'consistent'
              : 'mixed',
            ...(uniqueCaptureDates.length > 0
              ? {
                capture_date_min: uniqueCaptureDates[0],
                capture_date_max: uniqueCaptureDates[uniqueCaptureDates.length - 1],
              }
              : {}),
            sources,
            selections: contextSelections,
          },
        };
        const fingerprint = JSON.stringify(requestWithoutUuid);
        let submissionIdentity = submissionIdentityRef.current;
        if (!submissionIdentity || submissionIdentity.fingerprint !== fingerprint) {
          submissionIdentity = {
            fingerprint,
            uuid: crypto.randomUUID(),
          };
          submissionIdentityRef.current = submissionIdentity;
        }
        submissionUuid = submissionIdentity.uuid;
        studyEyeRequest = {
          ...requestWithoutUuid,
          submission_uuid: submissionUuid,
        };
      }

      let retryStudyEyeAfterImaging = false;
      if (studyEyeRequest) {
        try {
          await recordStudyEyeSelection(imageSyncService, studyEyeRequest);
        } catch (error) {
          if (!isTechnicalStudyEyeError(error)) {
            setSubmitError(getStudyEyeRequestErrorMessage(error));
            return;
          }
          retryStudyEyeAfterImaging = true;
        }
      }

      let result: { success: boolean } | null = null;
      let imagingError: unknown = null;
      // Fire the (synchronous, ~1 min) package build and poll its S3-written
      // progress in parallel so the overlay can show a real determinate bar.
      const progressId = crypto.randomUUID();
      setSubmitProgress(null);
      let progressPolling = true;
      const pollProgress = async () => {
        while (progressPolling) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          if (!progressPolling) break;
          try {
            const p = await imageSyncService.getSelectProgress(progressId);
            if (progressPolling && p && p.total > 0) {
              setSubmitProgress({ done: p.done, total: p.total, phase: p.phase });
            }
          } catch {
            // Ignore; the overlay falls back to the indeterminate spinner.
          }
        }
      };
      void pollProgress();
      try {
        result = await imageSyncService.selectE2EOutput(selections, {
          interpolate_oct: needsInterpolation,
        }, progressId);
      } catch (error) {
        imagingError = error;
      } finally {
        progressPolling = false;
      }

      let studyEyeStillUnconfirmed = false;
      if (retryStudyEyeAfterImaging && studyEyeRequest) {
        try {
          await recordStudyEyeSelection(imageSyncService, studyEyeRequest);
        } catch {
          studyEyeStillUnconfirmed = true;
          setReconciliationWarning(
            `Study Eye recording could not be confirmed. Image submission continued. Reconciliation reference: ${submissionUuid}.`
          );
        }
      }

      if (imagingError) {
        setSubmitError('Failed to create the image package. Please try again.');
        return;
      }

      if (result?.success) {
        // select_e2e_output can resolve between progress polls, so the last poll
        // often lands mid-count (e.g. "5 of 6"). Snap the bar to 100% and hold
        // briefly so the completion is actually visible before forwarding.
        setSubmitProgress(prev => (prev ? { done: prev.total, total: prev.total, phase: 'complete' } : prev));
        await new Promise(resolve => setTimeout(resolve, 600));
        if (studyEyeStillUnconfirmed) {
          setHasSubmittedImages(true);
          return;
        }
        onComplete?.();
      } else {
        setSubmitError('Failed to create image package');
      }
    } catch {
      console.error('Image selection submission failed.');
      setSubmitError('An unexpected error occurred while creating the image package.');
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(null);
    }
  }, [
    claimedOperatorName,
    imageSyncService,
    hasSubmittedImages,
    isSelectionComplete,
    isStudyEyeValid,
    isSubmitting,
    onComplete,
    openSeries,
    selectionOD,
    selectionOS,
    sessionUuid,
    studyEye,
    studyEyeEnabled,
    studyEyeReason,
    currentStudyEye,
  ]);

  const loadSeriesFromManifest = useCallback(async (
    e2eFilename: string,
    manifest: E2EManifest
  ) => {
    manifestVersionBySourceRef.current.set(e2eFilename, manifest.version);
    const flatOCT: any[] = [];
    const flatFAF: any[] = [];
    
    for (const s of manifest.series) {
      if (s.suggested_type === 'OCT') {
        const paths = s.combined_paths || [];
        if (paths.length === 0) continue;
        
        const mid = Math.floor(paths.length / 2);
        flatOCT.push({
          source: e2eFilename,
          previewPath: paths[mid],
          ...s,
        });
      } else {
        const paths = s.fundus_paths || [];
        if (paths.length === 0) continue;
        
        for (let i = 0; i < paths.length; i++) {
          flatFAF.push({
            source: e2eFilename,
            previewPath: paths[i],
            series_id: paths.length > 1 ? `${s.series_id}_img${i}` : s.series_id,
            original_series_id: s.series_id,
            image_index: i,
            total_images: paths.length,
            eye: s.eye,
            capture_date: s.capture_date,
            capture_datetime: s.capture_datetime,
            modality: s.modality,
            suggested_type: s.suggested_type,
            location_hint: s.location_hint,
            enface_mode: s.enface_mode,
            dimensions: s.dimensions,
            fundus_paths: [paths[i]],
          });
        }
      }
    }
    
    const allItems = [...flatOCT, ...flatFAF];
    if (allItems.length === 0) return;
    
    const iMap = common.buildMap(allItems, x => x.previewPath);
    const pathsToFetch = [...iMap.keys()];
    
    if (pathsToFetch.length > 0) {
      try {
        const pre = await imageSyncService.viewProcessed(pathsToFetch);
        for (const p of pre) {
          const s = iMap.get(p.path);
          if (s) s.previewURL = p.url;
        }
      } catch (err) {
        console.warn('Failed to fetch preview URLs:', err);
      }
    }
    
    return allItems;
  }, [imageSyncService]);

  const openedFilesRef = useRef<Set<string>>(new Set());
  const pendingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (pendingIntervalRef.current) {
        clearInterval(pendingIntervalRef.current);
      }
    };
  }, []);

  const doOpen = useCallback(async (filename: string) => {
    if (openedFilesRef.current.has(filename)) {
      return;
    }
    openedFilesRef.current.add(filename);

    try {
      const outputs = await imageSyncService.getProcessed([filename]);
      if (!outputs?.[0]?.manifest) return;
      
      const series = await loadSeriesFromManifest(filename, outputs[0].manifest);
      if (series && series.length > 0) {
        unstable_batchedUpdates(() => {
          setOpenSeries(prev => {
            const existingIds = new Set(prev.map(s => s.series_id));
            const newSeries = series.filter(s => !existingIds.has(s.series_id));
            if (newSeries.length === 0) return prev;
            return [...prev, ...newSeries];
          });
        });
      }
    } catch (err) {
      console.error('Failed to open E2E output:', err);
      openedFilesRef.current.delete(filename);
    }
  }, [imageSyncService, loadSeriesFromManifest]);

  const startProcessingPoll = useCallback((filenames: string[]) => {
    if (pendingIntervalRef.current) {
      clearInterval(pendingIntervalRef.current);
    }
    
    const checkStatus = async () => {
      try {
        const results = await imageSyncService.checkProcessed(filenames);
        const newlyComplete: string[] = [];
        
        setProcessingFiles(prev => {
          const updated = [...prev];
          for (const result of results) {
            const idx = updated.findIndex(f => f.filename === result.filename);
            if (idx === -1) continue;
            
            const wasAlreadyComplete = updated[idx].status === 'complete';
            
            if (result.status === 'complete') {
              updated[idx] = { ...updated[idx], status: 'complete', seriesCount: result.seriesCount };
              if (!wasAlreadyComplete) newlyComplete.push(result.filename);
            } else if (result.status === 'error') {
              updated[idx] = { ...updated[idx], status: 'error', error: 'Processing failed' };
            } else if (result.status === 'processing') {
              updated[idx] = { ...updated[idx], status: 'processing' };
            }
          }
          return updated;
        });
        
        for (const filename of newlyComplete) {
          doOpen(filename);
        }
        
        const allDone = results.every(r => r.status === 'complete' || r.status === 'error');
        if (allDone && pendingIntervalRef.current) {
          clearInterval(pendingIntervalRef.current);
          pendingIntervalRef.current = null;
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };
    
    checkStatus();
    pendingIntervalRef.current = setInterval(checkStatus, 5000);
  }, [imageSyncService, doOpen]);

  useEffect(() => {
    const loadExisting = async () => {
      try {
        setIsLoading(true);
        
        const pollResults = await imageSyncService.checkProcessed();
        
        if (!pollResults || pollResults.length === 0) {
          setIsLoading(false);
          return;
        }
        
        const completedFilenames: string[] = [];
        const inProgressFiles: ProcessingFile[] = [];
        
        for (const file of pollResults) {
          if (file.status === 'complete') {
            completedFilenames.push(file.filename);
            openedFilesRef.current.add(file.filename);
            // Recover upload provenance on the resume/reload path. sourceSha1ByRef
            // is otherwise only filled during a live upload, so a submission made
            // after a reload dropped context.sources entirely. poll_e2e echoes the
            // stored x-amz-meta-sha1, letting us repopulate the map here.
            if (file.sha1) sourceSha1ByRef.current.set(file.filename, file.sha1);
          } else if (file.status === 'processing' || file.status === 'pending') {
            inProgressFiles.push({
              filename: file.filename,
              displayName: file.filename,
              status: file.status === 'processing' ? 'processing' : 'pending',
              startedAt: file.uploaded_at ? new Date(file.uploaded_at).getTime() : Date.now(),
            });
          }
        }
        
        if (inProgressFiles.length > 0) {
          setProcessingFiles(inProgressFiles);
          startProcessingPoll(inProgressFiles.map(f => f.filename));
        }
        
        if (completedFilenames.length === 0) {
          setIsLoading(false);
          return;
        }
        
        const outputs = await imageSyncService.getProcessed(completedFilenames);
        const withManifests = outputs.filter(o => o.manifest && !o.error);
        
        if (withManifests.length === 0) {
          setIsLoading(false);
          return;
        }
        
        const allSeries: any[] = [];
        const pathsToFetch: string[] = [];
        
        for (const output of withManifests) {
          const manifest = output.manifest;
          manifestVersionBySourceRef.current.set(output.filename, manifest.version);
          
          for (const s of manifest.series) {
            if (s.suggested_type === 'OCT') {
              const paths = s.combined_paths || [];
              if (paths.length === 0) continue;
              
              const mid = Math.floor(paths.length / 2);
              const item = { source: output.filename, previewPath: paths[mid], ...s };
              allSeries.push(item);
              pathsToFetch.push(paths[mid]);
            } else {
              const paths = s.fundus_paths || [];
              if (paths.length === 0) continue;
              
              for (let i = 0; i < paths.length; i++) {
                const item = {
                  source: output.filename,
                  previewPath: paths[i],
                  series_id: paths.length > 1 ? `${s.series_id}_img${i}` : s.series_id,
                  original_series_id: s.series_id,
                  image_index: i,
                  total_images: paths.length,
                  eye: s.eye,
                  capture_date: s.capture_date,
                  capture_datetime: s.capture_datetime,
                  modality: s.modality,
                  suggested_type: s.suggested_type,
                  location_hint: s.location_hint,
                  enface_mode: s.enface_mode,
                  dimensions: s.dimensions,
                  fundus_paths: [paths[i]],
                };
                allSeries.push(item);
                pathsToFetch.push(paths[i]);
              }
            }
          }
        }
        
        if (pathsToFetch.length > 0) {
          const iMap = common.buildMap(allSeries, x => x.previewPath);
          const urls = await imageSyncService.viewProcessed(pathsToFetch);
          for (const p of urls) {
            const s = iMap.get(p.path);
            if (s) s.previewURL = p.url;
          }
        }
        
        setOpenSeries(allSeries);
        
      } catch (err) {
        console.error('Failed to load existing E2E files:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadExisting();
  }, [imageSyncService, startProcessingPoll]);

  const doUpload = useCallback(async (filesToUpload: File[]) => {
    const items: E2ELocal[] = filesToUpload.map(f => ({ file: f, sha1: '' }));

    setUploadProgress(filesToUpload.map(f => ({ filename: f.name, status: 'hashing', progress: 0 })));

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      setUploadProgress(prev => prev.map((p, idx) => idx === i ? { ...p, status: 'hashing', progress: 50 } : p));
      
      const buf = await item.file.arrayBuffer();
      const w = WordArray.create(buf);
      item.sha1 = sha1(w).toString(Hex);
      
      setUploadProgress(prev => prev.map((p, idx) => idx === i ? { ...p, progress: 100 } : p));
    }

    // Request an authoritative, collision-safe upload target for each file and
    // upload sequentially. Sequential ordering means two files sharing a name
    // within one batch can't race to the same key: the second sees the first
    // already in S3 and is assigned a distinct, content-derived name.
    const toPoll: string[] = [];
    for (const item of items) {
      const res = await imageSyncService.requestUploadUrlE2E(
        item.file.name, item.sha1, item.file.size, abortCon.current.signal)
        .catch((err: any) => {
          setUploadProgress(prev => prev.map(p => p.filename === item.file.name
            ? { ...p, status: 'error', error: err.message } : p));
          return null;
        });
      if (!res) continue;
      item.dest = res.finalFilename;
      sourceSha1ByRef.current.set(res.finalFilename, item.sha1);

      if (res.status === 'exists') {
        // Identical content already uploaded/processed; nothing to send.
        setUploadProgress(prev => prev.map(p => p.filename === item.file.name
          ? { ...p, status: 'uploaded', progress: 100 } : p));
        toPoll.push(res.finalFilename);
        continue;
      }

      setUploadProgress(prev => prev.map(p => p.filename === item.file.name
        ? { ...p, status: 'uploading', progress: 0 } : p));
      try {
        await uploadWithProgress(
          res.presigned_info,
          item.file,
          (progress) => {
            setUploadProgress(prev => prev.map(p => p.filename === item.file.name ? { ...p, progress } : p));
          }
        );
        setUploadProgress(prev => prev.map(p => p.filename === item.file.name
          ? { ...p, status: 'uploaded', progress: 100 } : p));
        toPoll.push(res.finalFilename);
      } catch (err: any) {
        setUploadProgress(prev => prev.map(p => p.filename === item.file.name
          ? { ...p, status: 'error', error: err.message } : p));
      }
    }

    if (toPoll.length > 0) {
      const processingEntries: ProcessingFile[] = toPoll.map((dest) => ({
        filename: dest,
        displayName: items.find(it => it.dest === dest)?.file.name ?? dest,
        status: 'pending',
        startedAt: Date.now(),
      }));

      setProcessingFiles(prev => [...prev, ...processingEntries]);
      startProcessingPoll(toPoll);
    }

    setTimeout(() => setUploadProgress([]), 2000);
  }, [imageSyncService, startProcessingPoll]);

  const handleImport = useCallback((imported: File[]) => {
    setFiles(value => value.concat(imported));
  }, []);

  const handleClickClear = useCallback(() => {
    setFiles([]);
  }, []);

  const handleClickUpload = useCallback(async () => {
    setIsUploading(true);
    try {
      await doUpload(files);
    } finally {
      setIsUploading(false);
      setFiles([]);
    }
  }, [files, doUpload]);

  const handleSelectOCT = useCallback((id: string | null, eye: 'OD' | 'OS') => {
    if (eye === 'OD')
      selectionOD.octVolume[1](id);
    else if (eye === 'OS')
      selectionOS.octVolume[1](id);
  }, [selectionOD.octVolume, selectionOS.octVolume]);

  const handleSelectFAF = useCallback((field: 'field2' | 'field1M', id: string | null, eye: 'OD' | 'OS') => {
    const selGroup = eye === 'OD' ? selectionOD : selectionOS;
    if (field === 'field2')
      selGroup.fafField2.setter(id);
    else if (field === 'field1M')
      selGroup.fafField1M.setter(id);
  }, [selectionOD, selectionOS]);

  // Sort series
  const sorted = useMemo(() => {
    const sortOCT = (items: any[]) => {
      return [...items].sort((a, b) => (b.slice_count || 0) - (a.slice_count || 0));
    };
    
    const sortFAF = (items: any[]) => {
      return [...items].sort((a, b) => {
        const aIsFAF = a.modality === 'FAF' ? 0 : 1;
        const bIsFAF = b.modality === 'FAF' ? 0 : 1;
        if (aIsFAF !== bIsFAF) return aIsFAF - bIsFAF;
        const timeA = a.capture_datetime || '';
        const timeB = b.capture_datetime || '';
        return timeA.localeCompare(timeB);
      });
    };

    const odOct = sortOCT(openSeries.filter(s => s.eye == 'OD' && s.suggested_type == 'OCT'));
    const odFaf = sortFAF(openSeries.filter(s => s.eye == 'OD' && s.suggested_type != 'OCT'));
    const osOct = sortOCT(openSeries.filter(s => s.eye == 'OS' && s.suggested_type == 'OCT'));
    const osFaf = sortFAF(openSeries.filter(s => s.eye == 'OS' && s.suggested_type != 'OCT'));

    return {
      OD: { oct: odOct, faf: odFaf },
      OS: { oct: osOct, faf: osFaf },
    };
  }, [openSeries]);

  const handleDismissProcessing = useCallback((filename: string) => {
    setProcessingFiles(prev => prev.filter(f => f.filename !== filename));
  }, []);

  const handleDismissAllComplete = useCallback(() => {
    setProcessingFiles(prev => prev.filter(f => f.status !== 'complete'));
  }, []);

  const isBusy = isUploading || isSubmitting;

  const overallUploadProgress = useMemo(() => {
    if (uploadProgress.length === 0) return null;
    const total = uploadProgress.reduce((sum, p) => sum + p.progress, 0);
    return Math.round(total / uploadProgress.length);
  }, [uploadProgress]);

  return <div className={`flex flex-col gap-4 ${isBusy ? 'pointer-events-none opacity-75' : ''}`}>
    {/* Loading indicator */}
    {isLoading && (
      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-md border border-gray-200">
        <Icon icon='eos-icons:loading' className='w-8 h-8 text-blue-600 animate-spin' />
        <span className="ml-3 text-gray-600">Loading existing E2E files...</span>
      </div>
    )}

    {/* Upload Area */}
    <E2EDropArea
      files={files}
      isUploading={isUploading}
      onImport={handleImport}
      onUpload={handleClickUpload}
      onClear={handleClickClear}
    />

    {/* Upload Progress */}
    {uploadProgress.length > 0 && (
      <UploadProgressPanel items={uploadProgress} overallProgress={overallUploadProgress} />
    )}

    {/* Processing Status */}
    {processingFiles.length > 0 && (
      <ProcessingStatusPanel 
        items={processingFiles}
        onDismiss={handleDismissProcessing}
        onDismissAllComplete={handleDismissAllComplete}
      />
    )}

    {/* Selection Buckets */}
    <div className='grid grid-cols-2 gap-4'>
      <SelectorBucket
        eye='OD'
        imageSyncService={imageSyncService}
        octItems={sorted.OD.oct}
        fafItems={sorted.OD.faf}
        selectedOCT={selectionOD.octVolume[0]}
        selectedField2={selectionOD.fafField2.value}
        selectedField1M={selectionOD.fafField1M.value}
        onSelectOCT={handleSelectOCT}
        onSelectFAF={handleSelectFAF}
      />
      <SelectorBucket
        eye='OS'
        imageSyncService={imageSyncService}
        octItems={sorted.OS.oct}
        fafItems={sorted.OS.faf}
        selectedOCT={selectionOS.octVolume[0]}
        selectedField2={selectionOS.fafField2.value}
        selectedField1M={selectionOS.fafField1M.value}
        onSelectOCT={handleSelectOCT}
        onSelectFAF={handleSelectFAF}
      />
    </div>

    {/* Submit Section */}
    {openSeries.length > 0 && (
      <SubmitPanel
        isSelectionComplete={isSelectionComplete}
        selectionCount={selectionCount}
        isSubmitting={isSubmitting}
        submitError={submitError}
        reconciliationWarning={reconciliationWarning}
        hasSubmittedImages={hasSubmittedImages}
        selectionOD={selectionOD}
        selectionOS={selectionOS}
        studyEyeEnabled={studyEyeEnabled}
        studyEye={studyEye}
        studyEyeReason={studyEyeReason}
        isStudyEyeValid={isStudyEyeValid}
        studyEyeReference={studyEyeReference}
        onStudyEyeChange={handleStudyEyeChange}
        onStudyEyeReasonChange={handleStudyEyeReasonChange}
        onSubmit={handleSubmit}
        onContinue={onComplete}
      />
    )}

    {/* Submitting Overlay */}
    {isSubmitting && (
      <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
        <div className='bg-white p-8 rounded-lg shadow-xl flex flex-col items-center gap-4 w-[22rem] max-w-[90vw]'>
          <Icon icon='eos-icons:loading' className='w-12 h-12 text-blue-600 animate-spin' />
          <div className='text-lg font-semibold'>Creating VVP Image Package...</div>
          {submitProgress && submitProgress.total > 0 ? (
            <div className='w-full flex flex-col gap-1.5'>
              <div className='h-2 w-full rounded-full bg-gray-200 overflow-hidden'>
                <div
                  className='h-full rounded-full bg-blue-600 transition-all duration-500 ease-out'
                  style={{ width: `${Math.min(100, Math.round((submitProgress.done / submitProgress.total) * 100))}%` }}
                />
              </div>
              <div className='text-sm text-gray-500 text-center'>
                {submitProgress.phase === 'complete'
                  ? 'Complete'
                  : submitProgress.phase === 'finalizing'
                  ? 'Finalizing records…'
                  : `Processing images… ${Math.min(submitProgress.done, submitProgress.total - 1)} of ${Math.max(1, submitProgress.total - 1)}`}
              </div>
            </div>
          ) : (
            <div className='text-sm text-gray-500 text-center'>
              Processing images and inserting database records.
              <br />
              This may take a minute.
            </div>
          )}
        </div>
      </div>
    )}
  </div>;
};


// =============================================================================
// Upload Progress Panel
// =============================================================================

interface UploadProgressPanelProps {
  items: UploadProgress[];
  overallProgress: number | null;
}

const UploadProgressPanel = ({ items, overallProgress }: UploadProgressPanelProps): JSX.Element => {
  const completedCount = items.filter(i => i.status === 'uploaded').length;
  const errorCount = items.filter(i => i.status === 'error').length;
  
  return (
    <div className='p-4 rounded-lg border border-blue-200 bg-blue-50'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <Icon icon='mdi:cloud-upload' className='w-5 h-5 text-blue-600' />
          <span className='font-medium text-blue-800'>Uploading Files</span>
        </div>
        <div className='text-sm text-blue-600'>
          {completedCount} of {items.length} complete
          {errorCount > 0 && <span className='text-red-500 ml-2'>({errorCount} failed)</span>}
        </div>
      </div>

      {overallProgress !== null && (
        <div className='mb-3'>
          <div className='h-2 bg-blue-200 rounded-full overflow-hidden'>
            <div className='h-full bg-blue-600 transition-all duration-300 ease-out' style={{ width: `${overallProgress}%` }} />
          </div>
        </div>
      )}

      <div className='flex flex-col gap-2'>
        {items.map((item) => (
          <div key={item.filename} className='flex items-center gap-3'>
            <div className='w-5 h-5 flex-shrink-0'>
              {item.status === 'hashing' && <Icon icon='mdi:file-search' className='w-5 h-5 text-gray-400 animate-pulse' />}
              {item.status === 'uploading' && <Icon icon='eos-icons:loading' className='w-5 h-5 text-blue-500 animate-spin' />}
              {item.status === 'uploaded' && <Icon icon='mdi:check-circle' className='w-5 h-5 text-green-500' />}
              {item.status === 'error' && <Icon icon='mdi:alert-circle' className='w-5 h-5 text-red-500' />}
            </div>
            <div className='flex-1 min-w-0'>
              <div className='text-sm text-gray-700 truncate'>{item.filename}</div>
              {item.status === 'error' && item.error && <div className='text-xs text-red-500'>{item.error}</div>}
            </div>
            <div className='w-24 flex-shrink-0'>
              {(item.status === 'hashing' || item.status === 'uploading') && (
                <div className='flex items-center gap-2'>
                  <div className='flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden'>
                    <div className='h-full bg-blue-500 transition-all duration-300' style={{ width: `${item.progress}%` }} />
                  </div>
                  <span className='text-xs text-gray-500 w-8'>{item.progress}%</span>
                </div>
              )}
              {item.status === 'uploaded' && <span className='text-xs text-green-600'>Complete</span>}
              {item.status === 'error' && <span className='text-xs text-red-600'>Failed</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// =============================================================================
// Processing Status Panel
// =============================================================================

interface ProcessingStatusPanelProps {
  items: ProcessingFile[];
  onDismiss: (filename: string) => void;
  onDismissAllComplete: () => void;
}

const ProcessingStatusPanel = ({ items, onDismiss, onDismissAllComplete }: ProcessingStatusPanelProps): JSX.Element => {
  const pendingCount = items.filter(i => i.status === 'pending').length;
  const processingCount = items.filter(i => i.status === 'processing').length;
  const completeCount = items.filter(i => i.status === 'complete').length;
  const errorCount = items.filter(i => i.status === 'error').length;
  const isAllComplete = pendingCount === 0 && processingCount === 0;

  return (
    <div className={`p-4 rounded-lg border ${isAllComplete ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          {!isAllComplete ? (
            <>
              <Icon icon='eos-icons:loading' className='w-5 h-5 text-amber-600 animate-spin' />
              <span className='font-medium text-amber-800'>Processing E2E Files</span>
            </>
          ) : (
            <>
              <Icon icon='mdi:check-circle' className='w-5 h-5 text-green-600' />
              <span className='font-medium text-green-800'>Processing Complete</span>
            </>
          )}
        </div>
        <div className='flex items-center gap-3'>
          <div className='text-sm'>
            {completeCount > 0 && <span className='text-green-600 mr-2'>{completeCount} done</span>}
            {(pendingCount + processingCount) > 0 && <span className='text-amber-600'>{pendingCount + processingCount} in progress</span>}
            {errorCount > 0 && <span className='text-red-500 ml-2'>{errorCount} failed</span>}
          </div>
          {completeCount > 0 && (
            <button onClick={onDismissAllComplete} className='text-xs text-gray-500 hover:text-gray-700'>Dismiss completed</button>
          )}
        </div>
      </div>
      <div className='flex flex-col gap-2'>
        {items.map((item) => <ProcessingFileRow key={item.filename} item={item} onDismiss={() => onDismiss(item.filename)} />)}
      </div>
    </div>
  );
};

const ProcessingFileRow = ({ item, onDismiss }: { item: ProcessingFile; onDismiss: () => void }): JSX.Element => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (item.status === 'pending' || item.status === 'processing') {
      const interval = setInterval(() => {
        if (item.startedAt) setElapsed(Math.floor((Date.now() - item.startedAt) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [item.status, item.startedAt]);

  const formatElapsed = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  return (
    <div className={`flex items-center gap-3 p-2 rounded-md ${item.status === 'complete' ? 'bg-green-100' : item.status === 'error' ? 'bg-red-100' : 'bg-white'}`}>
      <div className='w-6 h-6 flex-shrink-0'>
        {item.status === 'pending' && <div className='w-6 h-6 rounded-full border-2 border-gray-300 border-t-amber-500 animate-spin' />}
        {item.status === 'processing' && <Icon icon='eos-icons:loading' className='w-6 h-6 text-amber-500 animate-spin' />}
        {item.status === 'complete' && <Icon icon='mdi:check-circle' className='w-6 h-6 text-green-600' />}
        {item.status === 'error' && <Icon icon='mdi:alert-circle' className='w-6 h-6 text-red-600' />}
      </div>
      <div className='flex-1 min-w-0'>
        <div className='text-sm font-medium text-gray-800 truncate'>{item.displayName}</div>
        <div className='text-xs text-gray-500'>
          {item.status === 'pending' && 'Waiting to process...'}
          {item.status === 'processing' && `Processing... (${formatElapsed(elapsed)})`}
          {item.status === 'complete' && `${item.seriesCount || 0} series extracted`}
          {item.status === 'error' && (item.error || 'Processing failed')}
        </div>
      </div>
      {(item.status === 'pending' || item.status === 'processing') && (
        <div className='text-xs text-gray-400'>{formatElapsed(elapsed)}</div>
      )}
      {(item.status === 'complete' || item.status === 'error') && (
        <button onClick={onDismiss} className='p-1 text-gray-400 hover:text-gray-600'>
          <Icon icon='mdi:close' className='w-4 h-4' />
        </button>
      )}
    </div>
  );
};


// =============================================================================
// Submit Panel
// =============================================================================

interface SubmitPanelProps {
  isSelectionComplete: boolean;
  selectionCount: number;
  isSubmitting: boolean;
  submitError: string | null;
  reconciliationWarning: string | null;
  hasSubmittedImages: boolean;
  selectionOD: any;
  selectionOS: any;
  studyEyeEnabled: boolean;
  studyEye: StudyEyeValue | null;
  studyEyeReason: string;
  isStudyEyeValid: boolean;
  studyEyeReference: { label: string; value: StudyEyeValue } | null;
  onStudyEyeChange: (value: StudyEyeValue) => void;
  onStudyEyeReasonChange: (value: string) => void;
  onSubmit: () => void;
  onContinue?: () => void;
}

const SubmitPanel = ({
  isSelectionComplete,
  selectionCount,
  isSubmitting,
  submitError,
  reconciliationWarning,
  hasSubmittedImages,
  selectionOD,
  selectionOS,
  studyEyeEnabled,
  studyEye,
  studyEyeReason,
  isStudyEyeValid,
  studyEyeReference,
  onStudyEyeChange,
  onStudyEyeReasonChange,
  onSubmit,
  onContinue,
}: SubmitPanelProps): JSX.Element => {
  const isReadyToSubmit = isSelectionComplete && isStudyEyeValid && !hasSubmittedImages;
  const isNotAvailableReasonInvalid = studyEye === 'NA' && studyEyeReason.trim().length === 0;
  const progressCopy = hasSubmittedImages && reconciliationWarning
    ? 'Images submitted. Study Eye recording needs reconciliation.'
    : !isSelectionComplete
    ? `${selectionCount}/6 selections made. Select all required images to continue.`
    : studyEyeEnabled && studyEye === null
    ? '6/6 images selected. Study Eye is still needed.'
    : studyEyeEnabled && !isStudyEyeValid
    ? '6/6 images selected. A reason is still needed for Not Available.'
    : 'All images selected. Ready to submit.';

  return (
    <div className='flex flex-col gap-4 p-4 rounded-md border border-gray-300 bg-gray-50'>
      <div className='font-semibold text-gray-700'>Create VVP Image Package</div>

      {studyEyeEnabled && (
        <fieldset
          className='rounded-lg border border-sky-200 bg-white p-3'
          disabled={isSubmitting}
          aria-describedby='study-eye-description'
        >
          <legend className='px-1 font-semibold text-gray-800'>Study Eye</legend>
          <div className='flex flex-wrap items-start justify-between gap-2'>
            <p id='study-eye-description' className='text-sm text-gray-500'>
              Records the participant's study eye.
            </p>
            {studyEyeReference && (
              <div className='rounded-full bg-sky-50 px-3 py-1 text-sm font-medium text-sky-800'>
                {studyEyeReference.label}: {formatStudyEye(studyEyeReference.value)}
              </div>
            )}
          </div>

          <div className='mt-3 grid grid-cols-3 overflow-hidden rounded-md border border-gray-300'>
            {STUDY_EYE_OPTIONS.map(option => (
              <label
                key={option.value}
                className={`relative cursor-pointer border-r border-gray-300 px-3 py-2 text-center text-sm font-medium last:border-r-0 ${
                  studyEye === option.value
                    ? 'bg-sky-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-sky-50'
                }`}
              >
                <input
                  type='radio'
                  name='study-eye'
                  value={option.value}
                  checked={studyEye === option.value}
                  onChange={() => onStudyEyeChange(option.value)}
                  className='peer sr-only'
                />
                <span className='rounded-sm peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-4 peer-focus-visible:outline-sky-700'>
                  {option.label}
                </span>
              </label>
            ))}
          </div>

          {studyEye === 'NA' && (
            <div className='mt-3'>
              <label htmlFor='study-eye-reason' className='block text-sm font-medium text-gray-700'>
                Reason Study Eye is not available <span className='text-red-600'>*</span>
              </label>
              <textarea
                id='study-eye-reason'
                value={studyEyeReason}
                onChange={event => onStudyEyeReasonChange(event.target.value)}
                required
                minLength={1}
                maxLength={1000}
                rows={3}
                aria-invalid={isNotAvailableReasonInvalid}
                aria-describedby={isNotAvailableReasonInvalid
                  ? 'study-eye-reason-helper study-eye-reason-error'
                  : 'study-eye-reason-helper'}
                className={`mt-1 w-full resize-y rounded-md border px-3 py-2 text-sm text-gray-800 shadow-sm focus:outline-none focus:ring-2 ${
                  isNotAvailableReasonInvalid
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-sky-500 focus:ring-sky-200'
                }`}
              />
              <div id='study-eye-reason-helper' className='mt-1 flex justify-between gap-3 text-xs text-gray-500'>
                <span>Do not enter names, dates of birth, or other direct identifiers in reasons.</span>
                <span className='shrink-0'>{studyEyeReason.length}/1000</span>
              </div>
              {isNotAvailableReasonInvalid && (
                <div id='study-eye-reason-error' role='alert' className='mt-1 text-xs text-red-600'>
                  A reason is required when Study Eye is Not Available.
                </div>
              )}
            </div>
          )}

          {studyEye && studyEyeReference && studyEye !== studyEyeReference.value && (
            <div className='mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900'>
              The selected Study Eye differs from the {studyEyeReference.label.toLowerCase()}. This does not prevent image submission.
            </div>
          )}
        </fieldset>
      )}

      <div className='grid grid-cols-2 gap-2 text-sm'>
        <div className='flex flex-col gap-1'>
          <div className='font-medium text-gray-600'>OD Selections:</div>
          <SelectionCheckItem label='OCT Volume' selected={!!selectionOD.octVolume[0]} />
          <SelectionCheckItem label='FAF Field 2 (Macula)' selected={!!selectionOD.fafField2.value} />
          <SelectionCheckItem label='FAF Field 1M (ONH)' selected={!!selectionOD.fafField1M.value} />
        </div>
        <div className='flex flex-col gap-1'>
          <div className='font-medium text-gray-600'>OS Selections:</div>
          <SelectionCheckItem label='OCT Volume' selected={!!selectionOS.octVolume[0]} />
          <SelectionCheckItem label='FAF Field 2 (Macula)' selected={!!selectionOS.fafField2.value} />
          <SelectionCheckItem label='FAF Field 1M (ONH)' selected={!!selectionOS.fafField1M.value} />
        </div>
      </div>

      <div className='flex flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4'>
        <div className='text-sm text-gray-500'>{progressCopy}</div>
        <button
          type='button'
          onClick={onSubmit}
          disabled={!isReadyToSubmit || isSubmitting}
          className={`px-6 py-3 rounded-md font-semibold text-white transition-all duration-200 ${isReadyToSubmit && !isSubmitting ? 'bg-green-600 hover:bg-green-700 cursor-pointer' : 'bg-gray-400 cursor-not-allowed'}`}
        >
          {isSubmitting ? (
            <span className='flex items-center gap-2'>
              <Icon icon='eos-icons:loading' className='animate-spin' />
              Creating...
            </span>
          ) : 'Submit Selections'}
        </button>
      </div>

      {submitError && (
        <div className='p-3 rounded-md bg-red-100 border border-red-300 text-red-700'>
          <div className='font-semibold'>Error</div>
          <div className='text-sm'>{submitError}</div>
        </div>
      )}

      {reconciliationWarning && (
        <div role='status' className='flex items-center justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900'>
          <div>
            <div className='font-semibold'>Reconciliation needed</div>
            <div className='break-all text-sm'>{reconciliationWarning}</div>
          </div>
          {hasSubmittedImages && onContinue && (
            <button
              type='button'
              onClick={onContinue}
              className='shrink-0 rounded-md border border-amber-400 bg-white px-3 py-2 text-sm font-semibold hover:bg-amber-100'
            >
              Continue to Management
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function formatStudyEye(value: StudyEyeValue): string {
  return value === 'NA' ? 'Not Available' : value;
}

const SelectionCheckItem = ({ label, selected }: { label: string; selected: boolean }) => (
  <div className={selected ? 'text-green-600' : 'text-red-500'}>
    {selected ? '✓' : '○'} {label}
  </div>
);


// =============================================================================
// Selector Hook
// =============================================================================

function useE2ESelector(eye: 'OD' | 'OS') {
  const octVolume = useState<string | null>(null);
  const fafField2 = useState<string | null>(null);
  const fafField1M = useState<string | null>(null);

  const selectField2 = useCallback((id: string | null) => {
    // If selecting same as Field1M, clear Field1M
    if (id && id === fafField1M[0]) {
      fafField1M[1](null);
    }
    fafField2[1](id);
  }, [fafField1M[0]]);

  const selectField1M = useCallback((id: string | null) => {
    // If selecting same as Field2, clear Field2
    if (id && id === fafField2[0]) {
      fafField2[1](null);
    }
    fafField1M[1](id);
  }, [fafField2[0]]);

  return {
    octVolume,
    fafField2: { value: fafField2[0], setter: selectField2 },
    fafField1M: { value: fafField1M[0], setter: selectField1M },
  };
}


// =============================================================================
// Selector Bucket
// =============================================================================

interface SelectorBucketProps {
  eye: 'OD' | 'OS';
  imageSyncService: ImageSyncService;
  octItems: any[];
  fafItems: any[];
  selectedOCT: string | null;
  selectedField2: string | null;
  selectedField1M: string | null;
  onSelectOCT: (id: string | null, eye: 'OD' | 'OS') => void;
  onSelectFAF: (field: 'field2' | 'field1M', id: string | null, eye: 'OD' | 'OS') => void;
}

type LightboxSection = 'oct' | 'field2' | 'field1M';

interface LightboxState {
  section: LightboxSection;
  initialIndex: number;
}

const SelectorBucket = ({
  eye,
  imageSyncService,
  octItems,
  fafItems,
  selectedOCT,
  selectedField2,
  selectedField1M,
  onSelectOCT,
  onSelectFAF,
}: SelectorBucketProps): JSX.Element => {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const lightboxItems = lightbox?.section === 'oct' ? octItems : fafItems;
  const lightboxSelectedId = lightbox?.section === 'oct'
    ? selectedOCT
    : lightbox?.section === 'field2'
    ? selectedField2
    : selectedField1M;

  const handleLightboxSelect = useCallback((id: string | null) => {
    if (!lightbox) return;
    if (lightbox.section === 'oct') {
      onSelectOCT(id, eye);
      return;
    }
    onSelectFAF(lightbox.section, id, eye);
  }, [eye, lightbox, onSelectFAF, onSelectOCT]);

  return (
    <>
      <div className={`flex flex-col rounded-lg border-2 ${eye === 'OD' ? 'border-blue-200' : 'border-purple-200'} bg-white overflow-hidden`}>
        {/* Header */}
        <div className={`px-3 py-2 ${eye === 'OD' ? 'bg-blue-100 border-b border-blue-200' : 'bg-purple-100 border-b border-purple-200'}`}>
          <span className={`font-bold text-lg ${eye === 'OD' ? 'text-blue-800' : 'text-purple-800'}`}>{eye}</span>
          <span className='text-sm text-gray-500 ml-2'>{eye === 'OD' ? '(Right Eye)' : '(Left Eye)'}</span>
        </div>

        {/* OCT Selection */}
        <div className='p-3 border-b border-gray-200'>
          <div className='flex items-center gap-2 mb-2'>
            <Icon icon='mdi:eye-scan' className='w-5 h-5 text-gray-600' />
            <span className='font-medium text-gray-700'>Select OCT Volume</span>
            {!selectedOCT && <span className='text-xs text-red-500'>Required</span>}
          </div>
          <div className='flex flex-col gap-2'>
            {octItems.length === 0 ? (
              <div className='text-sm text-gray-400 italic p-2'>No OCT volumes available</div>
            ) : (
              octItems.map((s, index) => (
                <OCTItem
                  key={s.series_id}
                  item={s}
                  eye={eye}
                  isSelected={selectedOCT === s.series_id}
                  onSelect={onSelectOCT}
                  onExpand={() => setLightbox({ section: 'oct', initialIndex: index })}
                />
              ))
            )}
          </div>
        </div>

        {/* FAF Field 2 (Macula) Selection */}
        <div className='p-3 border-b border-gray-200'>
          <div className='flex items-center gap-2 mb-2'>
            <Icon icon='mdi:image' className='w-5 h-5 text-blue-600' />
            <span className='font-medium text-gray-700'>Select FAF - Field 2</span>
            <span className='text-xs text-gray-400'>(Macula)</span>
            {!selectedField2 && <span className='text-xs text-red-500 ml-auto'>Required</span>}
          </div>
          <div className='flex flex-wrap gap-2'>
            {fafItems.length === 0 ? (
              <div className='text-sm text-gray-400 italic p-2'>No FAF images available</div>
            ) : (
              fafItems.map((img, index) => (
                <FAFItem
                  key={img.previewPath}
                  item={img}
                  eye={eye}
                  isSelected={selectedField2 === img.previewPath}
                  isUsedElsewhere={selectedField1M === img.previewPath}
                  onSelect={(id) => onSelectFAF('field2', id, eye)}
                  onExpand={() => setLightbox({ section: 'field2', initialIndex: index })}
                />
              ))
            )}
          </div>
        </div>

        {/* FAF Field 1M (ONH) Selection */}
        <div className='p-3'>
          <div className='flex items-center gap-2 mb-2'>
            <Icon icon='mdi:image' className='w-5 h-5 text-purple-600' />
            <span className='font-medium text-gray-700'>Select FAF - Field 1M</span>
            <span className='text-xs text-gray-400'>(ONH)</span>
            {!selectedField1M && <span className='text-xs text-red-500 ml-auto'>Required</span>}
          </div>
          <div className='flex flex-wrap gap-2'>
            {fafItems.length === 0 ? (
              <div className='text-sm text-gray-400 italic p-2'>No FAF images available</div>
            ) : (
              fafItems.map((img, index) => (
                <FAFItem
                  key={img.previewPath}
                  item={img}
                  eye={eye}
                  isSelected={selectedField1M === img.previewPath}
                  isUsedElsewhere={selectedField2 === img.previewPath}
                  onSelect={(id) => onSelectFAF('field1M', id, eye)}
                  onExpand={() => setLightbox({ section: 'field1M', initialIndex: index })}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {lightbox && lightboxItems.length > 0 && (
        <PreviewLightbox
          eye={eye}
          imageSyncService={imageSyncService}
          kind={lightbox.section === 'oct' ? 'oct' : 'faf'}
          field={lightbox.section === 'oct' ? undefined : lightbox.section}
          items={lightboxItems}
          initialIndex={lightbox.initialIndex}
          selectedId={lightboxSelectedId}
          onSelect={handleLightboxSelect}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};


// =============================================================================
// OCT Item
// =============================================================================

// =============================================================================
// Preview image with bounded retry
// =============================================================================
// COMBINED_*.png / FUNDUS.png previews can briefly fail to load: the manifest
// may be published before every asset finishes uploading (S3 publish-order
// race), or an object may still carry the wrong Content-Type and be ORB-blocked.
// On <img> error, re-fetch a FRESH signed URL (a new presign, so the src truly
// changes) with bounded exponential backoff.
const PREVIEW_MAX_RETRIES = 3;

const PreviewImage = ({
  previewPath,
  previewURL,
  className,
  alt,
  placeholder,
  onExpand,
  wrapperClassName,
}: {
  previewPath?: string;
  previewURL?: string;
  className: string;
  alt: string;
  placeholder: JSX.Element;
  onExpand?: () => void;
  wrapperClassName?: string;
}): JSX.Element => {
  const { imageSyncService } = useContext(MainContext)!;
  const [url, setUrl] = useState<string | undefined>(previewURL);
  const retries = useRef(0);
  const inFlight = useRef(false);

  // Adopt (and reset the retry budget for) a URL the parent later supplies.
  useEffect(() => {
    setUrl(previewURL);
    retries.current = 0;
  }, [previewURL]);

  const handleError = useCallback(async () => {
    if (!previewPath || inFlight.current || retries.current >= PREVIEW_MAX_RETRIES) return;
    inFlight.current = true;
    const attempt = retries.current;
    retries.current += 1;
    try {
      await new Promise(resolve => setTimeout(resolve, 400 * Math.pow(2, attempt))); // 400 / 800 / 1600ms
      const urls = await imageSyncService.viewProcessed([previewPath]);
      const fresh = urls.find(u => u.path === previewPath)?.url;
      if (fresh) setUrl(fresh);
    } catch {
      // Bounded by PREVIEW_MAX_RETRIES; leave the last src in place.
    } finally {
      inFlight.current = false;
    }
  }, [previewPath, imageSyncService]);

  const preview = url
    ? <img src={url} className={className} alt={alt} onError={handleError} />
    : placeholder;

  if (!onExpand) return preview;

  return (
    <div className={`${wrapperClassName || 'relative inline-flex'} group`}>
      {preview}
      <button
        type='button'
        className='absolute bottom-1 right-1 w-6 h-6 rounded-md bg-black/45 text-white flex items-center justify-center shadow-sm opacity-50 transition duration-150 group-hover:opacity-100 hover:bg-black/80 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white'
        aria-label={`Open larger ${alt} preview`}
        title='Open larger preview'
        onClick={(event) => {
          event.stopPropagation();
          onExpand();
        }}
      >
        <Icon icon='mdi:magnify-plus-outline' className='w-3.5 h-3.5' />
      </button>
    </div>
  );
};


// =============================================================================
// Large preview lightbox
// =============================================================================

interface PreviewLightboxProps {
  eye: 'OD' | 'OS';
  imageSyncService: ImageSyncService;
  kind: 'oct' | 'faf';
  field?: 'field2' | 'field1M';
  items: any[];
  initialIndex: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onClose: () => void;
}

interface PreviewTransform {
  scale: number;
  x: number;
  y: number;
}

const MIN_PREVIEW_SCALE = 1;
const MAX_PREVIEW_SCALE = 8;

const PreviewLightbox = ({
  eye,
  imageSyncService,
  kind,
  field,
  items,
  initialIndex,
  selectedId,
  onSelect,
  onClose,
}: PreviewLightboxProps): JSX.Element => {
  const [activeIndex, setActiveIndex] = useState(() => (
    Math.min(Math.max(initialIndex, 0), Math.max(items.length - 1, 0))
  ));
  const [sliceIndex, setSliceIndex] = useState(0);
  const [sliceURLs, setSliceURLs] = useState<Array<string | undefined>>([]);
  const [fafURL, setFafURL] = useState<string | undefined>();
  const [isResolvingSource, setIsResolvingSource] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [previewTransform, setPreviewTransform] = useState<PreviewTransform>({
    scale: MIN_PREVIEW_SCALE,
    x: 0,
    y: 0,
  });
  const [isDragging, setIsDragging] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const sliderRef = useRef<HTMLInputElement>(null);
  const pathURLCache = useRef<Map<string, string>>(new Map());
  const imageRetries = useRef(0);
  const activeImagePathRef = useRef<string | undefined>();
  const previewTransformRef = useRef<PreviewTransform>(previewTransform);
  const dragState = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const currentItem = items[activeIndex] || items[0];
  const activeItemKey = kind === 'oct'
    ? currentItem?.series_id
    : currentItem?.previewPath;
  const slicePaths = useMemo<string[]>(() => {
    if (kind !== 'oct' || !currentItem) return [];
    if (currentItem.combined_paths?.length) return currentItem.combined_paths;
    return currentItem.previewPath ? [currentItem.previewPath] : [];
  }, [currentItem, kind]);
  const currentImagePath = kind === 'oct'
    ? slicePaths[sliceIndex]
    : currentItem?.previewPath;
  const currentImageURL = kind === 'oct'
    ? sliceURLs[sliceIndex]
    : fafURL;
  activeImagePathRef.current = currentImagePath;
  previewTransformRef.current = previewTransform;

  const scanCount = currentItem?.slice_count || currentItem?.combined_paths?.length || 0;
  const area = calcArea(currentItem?.scan_bounds_deg);
  const activeId = kind === 'oct' ? currentItem?.series_id : currentItem?.previewPath;
  const isSelected = Boolean(activeId && selectedId === activeId);
  const canSelect = kind === 'faf' || scanCount === 97;
  const captureTimeMatch = currentItem?.capture_datetime?.match(/T(\d{2}:\d{2}:\d{2})/);
  const captureTime = captureTimeMatch?.[1];
  const fieldLabel = field === 'field2' ? 'Field 2 (Macula)' : 'Field 1M (ONH)';
  const isOD = eye === 'OD';

  const resetZoom = useCallback(() => {
    setPreviewTransform({ scale: MIN_PREVIEW_SCALE, x: 0, y: 0 });
    dragState.current = null;
    setIsDragging(false);
  }, []);

  const clampPan = useCallback((scale: number, x: number, y: number): PreviewTransform => {
    if (scale <= MIN_PREVIEW_SCALE) {
      return { scale: MIN_PREVIEW_SCALE, x: 0, y: 0 };
    }

    const stage = stageRef.current;
    const image = imageRef.current;
    if (!stage || !image) return { scale, x, y };

    const maxX = Math.max(0, (image.clientWidth * scale - stage.clientWidth) / 2);
    const maxY = Math.max(0, (image.clientHeight * scale - stage.clientHeight) / 2);
    return {
      scale,
      x: Math.min(Math.max(x, -maxX), maxX),
      y: Math.min(Math.max(y, -maxY), maxY),
    };
  }, []);

  const zoomAt = useCallback((clientX: number, clientY: number, requestedScale: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();

    setPreviewTransform(previous => {
      const nextScale = Math.min(Math.max(requestedScale, MIN_PREVIEW_SCALE), MAX_PREVIEW_SCALE);
      if (nextScale === previous.scale) return previous;

      const cursorX = clientX - (rect.left + rect.width / 2);
      const cursorY = clientY - (rect.top + rect.height / 2);
      const ratio = nextScale / previous.scale;
      return clampPan(
        nextScale,
        cursorX - ratio * (cursorX - previous.x),
        cursorY - ratio * (cursorY - previous.y)
      );
    });
  }, [clampPan]);

  const zoomFromCenter = useCallback((factor: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    zoomAt(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
      previewTransform.scale * factor
    );
  }, [previewTransform.scale, zoomAt]);

  const showPrevious = useCallback(() => {
    setActiveIndex(index => Math.max(0, index - 1));
  }, []);

  const showNext = useCallback(() => {
    setActiveIndex(index => Math.min(items.length - 1, index + 1));
  }, [items.length]);

  const stepSlice = useCallback((amount: number) => {
    setSliceIndex(index => Math.min(Math.max(index + amount, 0), Math.max(slicePaths.length - 1, 0)));
  }, [slicePaths.length]);

  // Resolve every OCT slice only after its volume enters the lightbox. URLs are
  // cached for the lifetime of the modal so paging back to a volume is instant.
  useEffect(() => {
    let isActive = true;
    setSourceError(null);
    imageRetries.current = 0;

    if (kind === 'oct') {
      const middle = Math.floor(slicePaths.length / 2);
      setSliceIndex(middle);

      if (currentItem?.previewPath && currentItem.previewURL) {
        pathURLCache.current.set(currentItem.previewPath, currentItem.previewURL);
      }

      const cachedURLs = slicePaths.map(path => pathURLCache.current.get(path));
      setSliceURLs(cachedURLs);
      const missingPaths = slicePaths.filter(path => !pathURLCache.current.has(path));
      setIsResolvingSource(missingPaths.length > 0);

      if (missingPaths.length === 0) return () => { isActive = false; };

      imageSyncService.viewProcessed(missingPaths)
        .then(results => {
          if (!isActive) return;
          results.forEach(result => pathURLCache.current.set(result.path, result.url));
          const resolvedURLs = slicePaths.map(path => pathURLCache.current.get(path));
          setSliceURLs(resolvedURLs);
          if (!resolvedURLs.some(Boolean)) {
            setSourceError('The OCT slices could not be loaded.');
          }
        })
        .catch(() => {
          if (isActive) setSourceError('The OCT slices could not be loaded.');
        })
        .finally(() => {
          if (isActive) setIsResolvingSource(false);
        });
    } else {
      const path = currentItem?.previewPath;
      const suppliedURL = currentItem?.previewURL;
      if (path && suppliedURL) pathURLCache.current.set(path, suppliedURL);
      const cachedURL = path ? pathURLCache.current.get(path) : undefined;
      setFafURL(cachedURL);
      setIsResolvingSource(Boolean(path && !cachedURL));

      if (!path || cachedURL) return () => { isActive = false; };

      imageSyncService.viewProcessed([path])
        .then(results => {
          if (!isActive) return;
          const freshURL = results.find(result => result.path === path)?.url;
          if (!freshURL) {
            setSourceError('The preview could not be loaded.');
            return;
          }
          pathURLCache.current.set(path, freshURL);
          setFafURL(freshURL);
        })
        .catch(() => {
          if (isActive) setSourceError('The preview could not be loaded.');
        })
        .finally(() => {
          if (isActive) setIsResolvingSource(false);
        });
    }

    return () => {
      isActive = false;
    };
  }, [activeItemKey, currentItem, imageSyncService, kind, slicePaths]);

  useEffect(() => {
    resetZoom();
  }, [activeItemKey, resetZoom]);

  useEffect(() => {
    imageRetries.current = 0;
    setSourceError(null);
    setIsImageLoading(Boolean(currentImageURL));
  }, [currentImagePath, currentImageURL]);

  useEffect(() => {
    if (kind !== 'oct') return;
    [sliceIndex - 1, sliceIndex + 1].forEach(index => {
      const url = sliceURLs[index];
      if (!url) return;
      const neighbor = new Image();
      neighbor.src = url;
    });
  }, [kind, sliceIndex, sliceURLs]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * 0.002);
      zoomAt(
        event.clientX,
        event.clientY,
        previewTransformRef.current.scale * factor
      );
    };

    stage.addEventListener('wheel', handleWheel, { passive: false });
    return () => stage.removeEventListener('wheel', handleWheel);
  }, [zoomAt]);

  const handleLargeImageError = useCallback(async () => {
    const path = currentImagePath;
    if (!path || imageRetries.current >= PREVIEW_MAX_RETRIES) {
      setIsImageLoading(false);
      setSourceError('The preview could not be loaded.');
      return;
    }

    const attempt = imageRetries.current;
    imageRetries.current += 1;
    try {
      await new Promise(resolve => setTimeout(resolve, 400 * Math.pow(2, attempt)));
      const results = await imageSyncService.viewProcessed([path]);
      const freshURL = results.find(result => result.path === path)?.url;
      if (!freshURL || activeImagePathRef.current !== path) return;

      pathURLCache.current.set(path, freshURL);
      if (kind === 'oct') {
        setSliceURLs(urls => urls.map((url, index) => (
          slicePaths[index] === path ? freshURL : url
        )));
      } else {
        setFafURL(freshURL);
      }
    } catch {
      if (imageRetries.current >= PREVIEW_MAX_RETRIES) {
        setIsImageLoading(false);
        setSourceError('The preview could not be loaded.');
      }
    }
  }, [currentImagePath, imageSyncService, kind, slicePaths]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = originalOverflow;
      previouslyFocused?.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }

      const sliderFocused = document.activeElement === sliderRef.current;
      if (sliderFocused) {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          stepSlice(event.key === 'ArrowUp' ? 1 : -1);
        }
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNext();
      } else if (kind === 'oct' && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();
        stepSlice(event.key === 'ArrowUp' ? 1 : -1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [kind, onClose, showNext, showPrevious, stepSlice]);

  const handleDoubleClick = useCallback((event: React.MouseEvent<HTMLImageElement>) => {
    event.preventDefault();
    if (previewTransform.scale > MIN_PREVIEW_SCALE) {
      resetZoom();
    } else {
      zoomAt(event.clientX, event.clientY, 2.5);
    }
  }, [previewTransform.scale, resetZoom, zoomAt]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (
      previewTransform.scale <= MIN_PREVIEW_SCALE ||
      event.button !== 0 ||
      (event.target as HTMLElement).closest('button, input')
    ) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    dragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: previewTransform.x,
      originY: previewTransform.y,
    };
    setIsDragging(true);
  }, [previewTransform]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setPreviewTransform(previous => clampPan(
      previous.scale,
      drag.originX + event.clientX - drag.startX,
      drag.originY + event.clientY - drag.startY
    ));
  }, [clampPan]);

  const endPointerDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (dragState.current?.pointerId !== event.pointerId) return;
    dragState.current = null;
    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  if (!currentItem) return <></>;

  const title = kind === 'oct' ? 'OCT Volume Preview' : `FAF ${fieldLabel} Preview`;
  const imageAlt = kind === 'oct'
    ? `${eye} OCT volume, slice ${sliceIndex + 1} of ${slicePaths.length}`
    : `${eye} ${currentItem.modality || 'FAF'} ${fieldLabel} preview`;

  return createPortal(
    <div
      className='fixed inset-0 z-50 flex items-center justify-center p-3 bg-black bg-opacity-80'
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-label={title}
        className='w-[96vw] h-[94vh] max-w-[1600px] flex flex-col overflow-hidden rounded-xl bg-gray-900 text-white shadow-2xl ring-1 ring-white ring-opacity-20'
      >
        <div className='shrink-0 flex items-center gap-3 px-4 py-3 border-b border-gray-700 bg-gray-800'>
          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold ${isOD ? 'bg-blue-600' : 'bg-purple-600'}`}>
            {eye}
          </div>
          <div className='min-w-0'>
            <h2 className='font-semibold truncate'>{title}</h2>
            <div className='text-xs text-gray-400'>
              Candidate {activeIndex + 1} of {items.length}
            </div>
          </div>
          <div className='ml-auto flex items-center gap-2'>
            <button
              type='button'
              className='h-9 px-3 rounded-md bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-white text-sm flex items-center gap-1.5'
              aria-label='Reset preview zoom and pan'
              onClick={resetZoom}
            >
              <Icon icon='mdi:restore' className='w-4 h-4' />
              Reset
            </button>
            <button
              ref={closeButtonRef}
              type='button'
              className='w-9 h-9 rounded-md bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-white flex items-center justify-center'
              aria-label='Close larger preview'
              onClick={onClose}
            >
              <Icon icon='mdi:close' className='w-5 h-5' />
            </button>
          </div>
        </div>

        <div className='flex flex-col md:flex-row flex-1 min-h-0'>
          <div
            ref={stageRef}
            className={`relative flex-1 min-h-[240px] flex items-center justify-center overflow-hidden bg-black ${
              previewTransform.scale > MIN_PREVIEW_SCALE
                ? isDragging ? 'cursor-grabbing' : 'cursor-grab'
                : 'cursor-zoom-in'
            }`}
            style={{ touchAction: 'none' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endPointerDrag}
            onPointerCancel={endPointerDrag}
          >
            {currentImageURL && (
              <img
                ref={imageRef}
                src={currentImageURL}
                alt={imageAlt}
                draggable={false}
                className='max-w-full max-h-full object-contain select-none will-change-transform'
                style={{
                  transform: `translate(${previewTransform.x}px, ${previewTransform.y}px) scale(${previewTransform.scale})`,
                  transformOrigin: 'center center',
                }}
                onLoad={() => setIsImageLoading(false)}
                onError={() => void handleLargeImageError()}
                onDoubleClick={handleDoubleClick}
              />
            )}

            {(isResolvingSource || isImageLoading) && (
              <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-35 pointer-events-none' aria-live='polite'>
                <div className='px-4 py-3 rounded-lg bg-gray-900 bg-opacity-90 flex items-center gap-3 shadow-lg'>
                  <Icon icon='eos-icons:loading' className={`w-6 h-6 animate-spin ${isOD ? 'text-blue-400' : 'text-purple-400'}`} />
                  <span className='text-sm'>{kind === 'oct' ? 'Loading OCT slices…' : 'Loading preview…'}</span>
                </div>
              </div>
            )}

            {!isResolvingSource && !currentImageURL && (
              <div className='text-center text-gray-300 px-6' role='status'>
                <Icon icon='mdi:image-off' className='w-12 h-12 mx-auto mb-2 text-gray-500' />
                {sourceError || (kind === 'oct' ? 'This OCT slice could not be loaded.' : 'The preview could not be loaded.')}
              </div>
            )}

            <button
              type='button'
              className='absolute left-3 top-1/2 -translate-y-1/2 w-11 h-14 rounded-lg bg-gray-900 bg-opacity-70 hover:bg-opacity-95 disabled:opacity-25 disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white'
              aria-label='Show previous candidate'
              disabled={activeIndex === 0}
              onClick={showPrevious}
            >
              <Icon icon='mdi:chevron-left' className='w-8 h-8' />
            </button>
            <button
              type='button'
              className='absolute right-3 top-1/2 -translate-y-1/2 w-11 h-14 rounded-lg bg-gray-900 bg-opacity-70 hover:bg-opacity-95 disabled:opacity-25 disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white'
              aria-label='Show next candidate'
              disabled={activeIndex >= items.length - 1}
              onClick={showNext}
            >
              <Icon icon='mdi:chevron-right' className='w-8 h-8' />
            </button>

            <div className='absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-lg bg-gray-900 bg-opacity-80 p-1 shadow-lg'>
              <button
                type='button'
                className='w-8 h-8 rounded hover:bg-gray-700 disabled:opacity-40 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white'
                aria-label='Zoom out'
                disabled={previewTransform.scale <= MIN_PREVIEW_SCALE}
                onClick={() => zoomFromCenter(0.8)}
              >
                <Icon icon='mdi:magnify-minus-outline' className='w-5 h-5' />
              </button>
              <span className='w-14 text-center text-xs tabular-nums' aria-label={`Zoom ${Math.round(previewTransform.scale * 100)} percent`}>
                {Math.round(previewTransform.scale * 100)}%
              </span>
              <button
                type='button'
                className='w-8 h-8 rounded hover:bg-gray-700 disabled:opacity-40 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white'
                aria-label='Zoom in'
                disabled={previewTransform.scale >= MAX_PREVIEW_SCALE}
                onClick={() => zoomFromCenter(1.25)}
              >
                <Icon icon='mdi:magnify-plus-outline' className='w-5 h-5' />
              </button>
            </div>
          </div>

          <aside className='shrink-0 md:w-72 p-4 border-t md:border-t-0 md:border-l border-gray-700 bg-gray-800 overflow-y-auto'>
            <div className='text-xs uppercase tracking-wide text-gray-400 mb-3'>Image details</div>
            <dl className='space-y-3 text-sm'>
              <div>
                <dt className='text-gray-400'>Eye</dt>
                <dd className='font-medium'>{eye} · {isOD ? 'Right Eye' : 'Left Eye'}</dd>
              </div>
              <div>
                <dt className='text-gray-400'>Captured</dt>
                <dd className='font-medium'>
                  {currentItem.capture_date || 'Date unavailable'}{captureTime ? ` · ${captureTime}` : ''}
                </dd>
              </div>
              {kind === 'oct' ? (
                <>
                  <div>
                    <dt className='text-gray-400'>Slices</dt>
                    <dd className='font-medium'>{scanCount || slicePaths.length}</dd>
                  </div>
                  <div>
                    <dt className='text-gray-400'>Scan area</dt>
                    <dd className='font-medium'>{area ? `${area.x}° × ${area.y}°` : 'Unavailable'}</dd>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <dt className='text-gray-400'>Modality</dt>
                    <dd className='font-medium'>{currentItem.modality || 'FAF'}</dd>
                  </div>
                  <div>
                    <dt className='text-gray-400'>Field</dt>
                    <dd className='font-medium'>{fieldLabel}</dd>
                  </div>
                  <div>
                    <dt className='text-gray-400'>Image</dt>
                    <dd className='font-medium'>
                      {(currentItem.image_index ?? 0) + 1} / {currentItem.total_images || 1}
                    </dd>
                  </div>
                </>
              )}
            </dl>

            <div className='mt-5 pt-4 border-t border-gray-700'>
              <button
                type='button'
                className={`w-full px-4 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:bg-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed ${
                  isSelected
                    ? 'bg-gray-600 hover:bg-gray-500 focus:ring-gray-300'
                    : isOD
                    ? 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-400'
                    : 'bg-purple-600 hover:bg-purple-500 focus:ring-purple-400'
                }`}
                disabled={!canSelect || !activeId}
                aria-pressed={isSelected}
                onClick={() => onSelect(isSelected ? null : activeId)}
              >
                <Icon icon={isSelected ? 'mdi:close-circle-outline' : 'mdi:check-circle-outline'} className='w-5 h-5' />
                {canSelect ? (isSelected ? 'Deselect this' : 'Select this') : 'Not compatible'}
              </button>
            </div>

            <p className='mt-4 text-xs leading-relaxed text-gray-400'>
              Scroll to zoom, drag to pan, or double-click to toggle zoom. Use ← and → to page candidates.
            </p>
          </aside>
        </div>

        {kind === 'oct' && (
          <div className='shrink-0 px-4 py-3 border-t border-gray-700 bg-gray-800'>
            <div className='flex items-center gap-4'>
              <label htmlFor='oct-slice-slider' className='shrink-0 text-sm font-medium'>
                Slice {sliceIndex + 1} / {slicePaths.length}
              </label>
              <input
                ref={sliderRef}
                id='oct-slice-slider'
                type='range'
                min={0}
                max={Math.max(slicePaths.length - 1, 0)}
                step={1}
                value={sliceIndex}
                disabled={slicePaths.length <= 1}
                className={`w-full cursor-pointer disabled:cursor-not-allowed ${isOD ? 'accent-blue-500' : 'accent-purple-500'}`}
                aria-label={`OCT B-scan slice ${sliceIndex + 1} of ${slicePaths.length}`}
                onChange={(event) => setSliceIndex(Number(event.target.value))}
              />
              <span className='hidden sm:block shrink-0 text-xs text-gray-400'>Focus slider: ←/→ · Any time: ↑/↓</span>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};


interface OCTItemProps {
  item: any;
  eye: 'OD' | 'OS';
  isSelected: boolean;
  onSelect: (id: string | null, eye: 'OD' | 'OS') => void;
  onExpand: () => void;
}

const OCTItem = ({ item, eye, isSelected, onSelect, onExpand }: OCTItemProps): JSX.Element => {
  const scanCount = item.slice_count || item.combined_paths?.length || 0;
  const isSelectable = scanCount === 97;
  const area = calcArea(item.scan_bounds_deg);
  // const qualityPercent = item.quality_mean != null ? Math.round(item.quality_mean * 100) : null;

  const handleClick = useCallback(() => {
    if (!isSelectable) return;
    onSelect(isSelected ? null : item.series_id, eye);
  }, [isSelectable, isSelected, item.series_id, eye, onSelect]);

  if (!isSelectable) {
    // Grayed out, smaller version for non-97 slice scans
    return (
      <div className='flex flex-row p-2 gap-2 rounded-md border border-gray-200 bg-gray-100 opacity-60'>
        <div className='shrink-0 flex flex-col gap-0.5'>
          <div className='flex items-center gap-2'>
            <span className='text-sm text-gray-500'>{scanCount} slices</span>
            <span className='text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-500'>Not compatible</span>
          </div>
          <div className='text-xs text-gray-400'>{item.capture_date}</div>
        </div>
        <div className='flex-auto flex justify-end'>
          {/* No magnify affordance: non-97-slice volumes are Not compatible / unselectable. */}
          <PreviewImage
            previewPath={item.previewPath}
            previewURL={item.previewURL}
            className='h-12 w-auto object-contain rounded opacity-50'
            alt='OCT'
            placeholder={<div className='h-12 w-20 bg-gray-200 rounded' />}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      className={`flex flex-row p-2 gap-3 rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'border-2 border-blue-500 bg-blue-50 shadow-sm' 
          : 'border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
      }`}
    >
      <div className='shrink-0 flex flex-col gap-0.5'>
        <div className='flex items-center gap-2'>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
            {isSelected && <Icon icon='mdi:check' className='w-3 h-3 text-white' />}
          </div>
          <span className='font-semibold text-gray-800'>Volume Scan</span>
        </div>
        <div className='text-sm text-gray-600 ml-7'>
          {scanCount} slices {area && <span className='text-gray-400'>· {area.x}°×{area.y}°</span>}
        </div>
        <div className='text-xs text-gray-400 ml-7'>{item.capture_date}</div>
        {/* {qualityPercent != null && (
          <div className='flex items-center gap-1.5 ml-7 mt-1'>
            <span className='text-xs text-gray-500'>Quality:</span>
            <div className='w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden'>
              <div
                className={`h-full rounded-full ${qualityPercent >= 80 ? 'bg-green-500' : qualityPercent >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${qualityPercent}%` }}
              />
            </div>
            <span className='text-xs text-gray-500'>{qualityPercent}%</span>
          </div>
        )} */}
      </div>
      <div className='flex-auto flex justify-end'>
        <PreviewImage
          previewPath={item.previewPath}
          previewURL={item.previewURL}
          className='h-20 w-auto object-contain rounded shadow-sm'
          alt='OCT'
          onExpand={onExpand}
          placeholder={(
            <div className='h-20 w-32 bg-gray-200 rounded flex items-center justify-center'>
              <Icon icon='mdi:image-off' className='w-8 h-8 text-gray-400' />
            </div>
          )}
        />
      </div>
    </div>
  );
};


// =============================================================================
// FAF Item
// =============================================================================

interface FAFItemProps {
  item: any;
  eye: 'OD' | 'OS';
  isSelected: boolean;
  isUsedElsewhere: boolean;
  onSelect: (id: string | null) => void;
  onExpand: () => void;
}

const FAFItem = ({ item, eye, isSelected, isUsedElsewhere, onSelect, onExpand }: FAFItemProps): JSX.Element => {
  const handleClick = useCallback(() => {
    onSelect(isSelected ? null : item.previewPath);
  }, [isSelected, item.previewPath, onSelect]);

  const timeStr = useMemo(() => {
    if (!item.capture_datetime) return '';
    const match = item.capture_datetime.match(/T(\d{2}:\d{2}:\d{2})/);
    return match ? match[1] : '';
  }, [item.capture_datetime]);

  const dateStr = useMemo(() => {
    if (!item.capture_date) return '';
    return item.capture_date;
  }, [item.capture_date]);

  // const modalityLabel = item.modality && item.modality !== 'FAF' ? item.modality : null;
  const modality = item.modality;
  const isMultiImage = item.total_images && item.total_images > 1;

  return (
    <div
      onClick={handleClick}
      className={`flex flex-col p-1.5 rounded-lg cursor-pointer transition-all w-24 ${
        isSelected
          ? 'border-2 border-blue-500 bg-blue-50 shadow-sm'
          : isUsedElsewhere
          ? 'border-2 border-gray-300 bg-gray-100 opacity-50'
          : 'border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
      }`}
    >
      {/* Header row: time + modality */}
      <div className='flex items-center justify-between mb-1 text-xs'>
        
        {modality && (
          <span className='px-1 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px]'>{modality}</span>
        )}
      </div>
      
      {/* Image */}
      <div className='relative'>
        <PreviewImage
          previewPath={item.previewPath}
          previewURL={item.previewURL}
          className='w-full h-20 object-cover rounded'
          alt='FAF'
          onExpand={onExpand}
          wrapperClassName='relative block w-full'
          placeholder={(
            <div className='w-full h-20 bg-gray-200 rounded flex items-center justify-center'>
              <Icon icon='mdi:image-off' className='w-6 h-6 text-gray-400' />
            </div>
          )}
        />
        {/* Multi-image indicator */}
        {isMultiImage && (
          <div className='absolute bottom-1 left-1 text-[10px] px-1 py-0.5 rounded bg-black bg-opacity-60 text-white'>
            {(item.image_index ?? 0) + 1}/{item.total_images}
          </div>
        )}
        {/* Selection indicator */}
        {isSelected && (
          <div className='absolute top-1 right-1 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center'>
            <Icon icon='mdi:check' className='w-3 h-3 text-white' />
          </div>
        )}
        {/* Used elsewhere indicator */}
        {isUsedElsewhere && !isSelected && (
          <div className='absolute inset-0 bg-gray-500 bg-opacity-30 rounded flex items-center justify-center'>
            <span className='text-[10px] text-white bg-gray-700 px-1 rounded'>In use</span>
          </div>
        )}
        
      </div>
      <div className='text-gray-500 truncate text-[10px]'>{timeStr}<br/>{dateStr}</div>
    </div>
  );
};


// =============================================================================
// E2E Drop Area
// =============================================================================

interface E2EDropAreaProps {
  files: File[];
  isUploading: boolean;
  onImport: (files: File[]) => void;
  onClear: () => void;
  onUpload: () => void;
}

const E2EDropArea = ({ files, isUploading, onImport, onClear, onUpload }: E2EDropAreaProps): JSX.Element => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const onDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const fileEntries = await getDataTransferFiles(e.dataTransfer.items);
    const droppedFiles = await Promise.all(fileEntries.map(f => new Promise<File>(resolve => f.file(resolve))));
    const acceptedFiles = droppedFiles
      .filter(f => f.name.split('.').pop()?.toUpperCase() === 'E2E')
      .sort((a, b) => a.name.localeCompare(b.name));
    if (acceptedFiles.length > 0) onImport(acceptedFiles);
  }, [onImport]);

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
  }, []);

  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={`relative flex flex-col justify-center items-center border-2 rounded-lg p-6 gap-2 transition-all duration-300 ${
        isDraggingOver ? 'border-solid border-blue-500 bg-blue-50 scale-[1.02]' : 'border-dashed border-gray-300 bg-gray-50 hover:border-gray-400'
      }`}
    >
      <Icon icon='mdi:file-upload-outline' className={`w-12 h-12 transition-all ${isDraggingOver ? 'text-blue-500 -translate-y-1' : 'text-gray-400'}`} />
      <div className='text-center'>
        <div className={`font-semibold text-lg ${isDraggingOver ? 'text-blue-600' : 'text-gray-700'}`}>Import E2E Files</div>
        <div className='text-sm text-gray-500'>Drag & drop Heidelberg E2E files here</div>
      </div>
      
      {files.length > 0 && (
        <div className='mt-2 p-2 bg-white rounded-md border border-gray-200 w-full max-w-md'>
          <div className='text-xs font-medium text-gray-500 mb-1'>{files.length} file{files.length > 1 ? 's' : ''} ready:</div>
          <div className='flex flex-col gap-1 max-h-24 overflow-y-auto'>
            {files.map(f => (
              <div key={f.name} className='text-sm text-gray-700 flex items-center gap-1'>
                <Icon icon='mdi:file' className='w-4 h-4 text-gray-400' />
                <span className='truncate'>{f.name}</span>
                <span className='text-xs text-gray-400 ml-auto'>{formatFileSize(f.size)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className='flex gap-2 mt-2'>
        {files.length > 0 && (
          <>
            <button onClick={onClear} className='px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md'>Clear</button>
            <button onClick={onUpload} disabled={isUploading} className='px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-md'>
              Upload & Process
            </button>
          </>
        )}
      </div>
    </div>
  );
};


// =============================================================================
// Utilities
// =============================================================================

function calcArea(bounds: any) {
  if (!bounds?.x || !bounds?.y) return undefined;
  return {
    x: Math.round(bounds.x[1] - bounds.x[0]),
    y: Math.round(bounds.y[1] - bounds.y[0]),
  };
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploadWithProgress(
  presignedInfo: any,
  file: File,
  onProgress: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Submit the server-signed fields verbatim. The key is pinned server-side
    // and x-amz-meta-sha1 is already included in the POST policy, so we must
    // not add, rename, or override any field (extra fields violate the policy).
    const form = new FormData();
    for (const k in presignedInfo.fields) {
      form.append(k, presignedInfo.fields[k]);
    }
    form.append('file', file);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed: Network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.open('POST', presignedInfo.url);
    xhr.send(form);
  });
}
